import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { IconButton } from '@mui/material'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import FloorMapPreviewOverlay, { FloorMapRobotMarker, FloorMapTrailPoint } from 'components/FloorMapPreviewOverlay'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import { IOverlayItem } from 'components/MapboxMap/OverlayControl/OverlayControl'
import RobotOnMap from 'components/RobotOnMap/RobotOnMap'
import SetPoseConfirmCard from 'components/RobotOnMap/SetPoseConfirmCard/SetPoseConfirmCard'
import SetPoseFloatingButton from 'components/RobotOnMap/SetPoseFloatingButton/SetPoseFloatingButton'
import RobotOnMapEditable from 'components/RobotOnMapEditable/RobotOnMapEditable'
import { ROBOT_MAP_IMAGES, getRobotIconName } from 'components/RobotOnMapEditable/robotMapUtils'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import RobotTypeLabel from 'components/RobotTypeLabel/RobotTypeLabel'
import { IScenarioInfo } from 'components/SharedScenarioItem/SharedScenarioItem'
import Title from 'components/Title/Title'
import type { NormalizedStatisticsApiRow } from 'features/tasksNormalized/filterableRecord'
import { Point } from 'geojson'
import { useAutonomicsMappingLocalWebSocket } from 'hooks/useAutonomicsMappingLocalWebSocket'
import { useGausiumLiveExecutionWebSocket } from 'hooks/useGausiumLiveExecutionWebSocket'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useRobotSetPoseLocalization } from 'hooks/useRobotSetPoseLocalization'
import { canSeeRobotTasksTab, useUser } from 'hooks/user'
import GausiumMapGeoPinsOnMap, {
  GausiumMapGeoPin,
} from 'pages/Facility/FacilityView/GausiumMapGeoPinsOnMap/GausiumMapGeoPinsOnMap'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { useDispatch, useSelector } from 'react-redux'
import { RobotActions } from 'store/actions/robot'
import { selectRobot, selectRobotActiveMode, selectRobotTelemetry } from 'store/selectors/robot'
import { robotThunks } from 'store/thunks/robot'
import { GAUSIUM_ENABLED, ROBOT_MODES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { PROGRESS_TYPES } from 'utils/constants'
import { IMapboxElement } from 'utils/interfaces'
import { buildLaserscanOverlayItem } from 'utils/laserscanOverlayItem'
import { focusMapOnGeoPoints, resolveRobotGeoCoordinate, zoomMapTo } from 'utils/mapUtils'
import { readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { canSeeServiceReport } from 'utils/serviceReportForm'
import {
  GEO_MAP_LEFT_PANEL_INSET_PX,
  SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX,
  SCENARIO_MAP_COLUMN_INSET_LEFT_PX,
} from 'utils/scenarioMapLayout'
import { isSetPoseButtonDisabled, isSetPoseCardVisible } from 'utils/setPoseUi'
import { stringParam } from 'utils/urlQueryState'
import { VENDOR } from 'utils/vendorDisplayName'

import LaserscanLayer from './Layers/LaserscanLayer'
import LidarMapLayer from './Layers/LidarMapLayer'
import RobotImage from './RobotLeftBlock/RobotImage/RobotImage'
import RobotLeftBlock, {
  getRobotTabIndices,
  robotTabIndexFromName,
  robotTabNameFromIndex,
} from './RobotLeftBlock/RobotLeftBlock'
import ServiceCalibMap from './RobotLeftBlock/Service/ServiceCalibMap'
import { buildServiceCalibMapFeatures } from './RobotLeftBlock/Service/serviceCalibMapFeatures'
import { useServiceCalibCatalog } from './RobotLeftBlock/Service/useServiceCalibCatalog'

import style from './RobotView.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'

function parseNumericId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value.trim())
  return null
}

function gausiumMapRowToScenarioInfo(m: {
  id: number
  map_name?: string
  robot_type?: string
  subareas_json?: { partitions?: unknown[] }
  preview_url?: string | null
}): IScenarioInfo {
  const partitions = (m.subareas_json?.partitions as any[]) || []
  const subareas = partitions.map((p: any) => String(p?.name ?? '').trim()).filter((v: string) => !!v)
  return {
    id: m.id,
    name: m.map_name || '(no name)',
    missions_ids: subareas,
    missions_count: subareas.length,
    missions_statuses: ['ok'],
    robot_type: m.robot_type || '',
    is_gausium_map: true,
    preview_url: m.preview_url ?? null,
  }
}

const shouldRestoreLidarMapAfterMode = (mode?: ROBOT_MODES) =>
  mode === ROBOT_MODES.MODE_MAPPING ||
  mode === ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION ||
  mode === ROBOT_MODES.MODE_SWITCHING_FACILITY

const RobotView = () => {
  const { t } = useTranslation()
  const user = useUser()
  const isMobile = useIsMobile()
  const mapPanelOffset = isMobile ? 0 : GEO_MAP_LEFT_PANEL_INSET_PX
  const [showSidebar, setShowSidebar] = useState(true)
  const robot = useSelector(selectRobot)
  const activeMode = useSelector(selectRobotActiveMode)
  const mapRef = useRef<IMapboxElement | null>(null)
  const mapWrapperRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch<any>()
  const lidarMapLayer = useSelector((state: any) => state.robot.lidarMapLayer)

  const telemetry = useSelector(selectRobotTelemetry)
  const {
    setPoseMode,
    setSetPoseMode,
    setPosePosition,
    setSetPosePosition,
    setPoseYaw,
    setSetPoseYaw,
    laserscanActive,
    laserscanData,
    toggleLaserscan,
    handleConfirmSetPose,
  } = useRobotSetPoseLocalization({
    robotId: robot.robotInfo?.id,
    robotSerialNumber: robot.robotInfo?.serialNumber,
    localizationMode: telemetry?.localization_info?.localization_mode,
    livePosition: telemetry?.livePosition,
    lastPosition: robot.robotInfo?.lastPosition,
    liveOrientation: telemetry?.quaternion ?? null,
    lastOrientation: robot.robotInfo?.lastOrientation,
  })

  const switchShowLidarMap = () => {
    const show = !lidarMapLayer.show
    dispatch(RobotActions.setShowLidarMap(show))

    const mapName = robot.telemetry?.localization_info?.current_lidar_map
    const facilityId = robot.robotInfo?.facilityId ?? null
    if (show && mapName && robot.robotInfo) {
      // Re-fetch if not loaded yet, or if the map name changed since last load
      const currentLayerMatches =
        lidarMapLayer.layer?.name === mapName &&
        lidarMapLayer.layer?.robotId === robot.robotInfo.id &&
        lidarMapLayer.layer?.facilityId === facilityId
      const isCurrentlyWorkingOnThisMap =
        lidarMapLayer.loadingMapName === mapName &&
        lidarMapLayer.loadingRobotId === robot.robotInfo.id &&
        lidarMapLayer.loadingFacilityId === facilityId &&
        lidarMapLayer.loadingProgress === PROGRESS_TYPES.WORK
      if (
        (lidarMapLayer.loadingProgress !== PROGRESS_TYPES.SUCCESS || !currentLayerMatches) &&
        !isCurrentlyWorkingOnThisMap
      ) {
        dispatch(robotThunks.ensureLidarMapLayer(robot.robotInfo.id, mapName, facilityId))
      }
    }
  }

  const prevLocalizationMode = useRef<number | undefined>(undefined)
  const prevActiveMode = useRef<ROBOT_MODES | undefined>(undefined)
  const prevLidarMapName = useRef<string | undefined>(undefined)

  useEffect(() => {
    const localizationMode = telemetry?.localization_info?.localization_mode
    const mapName = telemetry?.localization_info?.current_lidar_map
    const facilityId = robot.robotInfo?.facilityId ?? null
    const lidarLocalizationAvailable = localizationMode === 1 || localizationMode === 3
    const lidarMapBecameAvailable = !!mapName && !prevLidarMapName.current
    const lidarMapChanged = !!mapName && !!prevLidarMapName.current && prevLidarMapName.current !== mapName

    if (!mapName) {
      if (lidarMapLayer.show) {
        dispatch(RobotActions.setShowLidarMap(false))
      }
      if (lidarMapLayer.layer || lidarMapLayer.loadingMapName) {
        dispatch(RobotActions.setLidarMapLayer(null))
        dispatch(RobotActions.setLidarMapLoadingState({ progress: PROGRESS_TYPES.IDLE, name: undefined }))
      }
      prevLocalizationMode.current = localizationMode
      prevActiveMode.current = activeMode
      prevLidarMapName.current = undefined
      return
    }

    // Force visibility when transitioning out of modes that temporarily hide the lidar map.
    if (
      shouldRestoreLidarMapAfterMode(prevActiveMode.current) &&
      !shouldRestoreLidarMapAfterMode(activeMode) &&
      lidarLocalizationAvailable
    ) {
      if (!lidarMapLayer.show) {
        dispatch(RobotActions.setShowLidarMap(true))
      }
    }

    if (lidarLocalizationAvailable) {
      // 1 = SLAM based, 3 = Combined (which includes SLAM)
      // Force visibility when transitioning into SLAM mode or when the robot confirms a lidar map update.
      if (
        !lidarMapLayer.show &&
        (prevLocalizationMode.current !== localizationMode || lidarMapBecameAvailable || lidarMapChanged)
      ) {
        dispatch(RobotActions.setShowLidarMap(true))
      }

      // Automatically switch map if it changed
      if (mapName && robot.robotInfo) {
        const currentLayerMatches =
          lidarMapLayer.layer?.name === mapName &&
          lidarMapLayer.layer?.robotId === robot.robotInfo.id &&
          lidarMapLayer.layer?.facilityId === facilityId
        const isNotLoaded = lidarMapLayer.loadingProgress === PROGRESS_TYPES.IDLE
        const isNewMap = !currentLayerMatches
        const isCurrentlyWorkingOnThisMap =
          lidarMapLayer.loadingMapName === mapName &&
          lidarMapLayer.loadingRobotId === robot.robotInfo.id &&
          lidarMapLayer.loadingFacilityId === facilityId &&
          (lidarMapLayer.loadingProgress === PROGRESS_TYPES.WORK ||
            lidarMapLayer.loadingProgress === PROGRESS_TYPES.ERROR)

        if ((isNotLoaded || isNewMap) && !isCurrentlyWorkingOnThisMap && lidarMapLayer.show) {
          dispatch(robotThunks.ensureLidarMapLayer(robot.robotInfo.id, mapName, facilityId))
        }
      }
    } else if (localizationMode === 2) {
      // 2 = GNSS based — hide lidar map; laserscan/set-pose handled by useRobotSetPoseLocalization
      if (lidarMapLayer.show && prevLocalizationMode.current !== localizationMode) {
        dispatch(RobotActions.setShowLidarMap(false))
      }
    }

    prevLocalizationMode.current = localizationMode
    prevActiveMode.current = activeMode
    prevLidarMapName.current = mapName
  }, [
    telemetry?.localization_info?.localization_mode,
    telemetry?.localization_info?.current_lidar_map,
    robot.robotInfo?.id,
    robot.robotInfo?.facilityId,
    lidarMapLayer.show,
    lidarMapLayer.layer?.name,
    lidarMapLayer.layer?.robotId,
    lidarMapLayer.layer?.facilityId,
    lidarMapLayer.loadingProgress,
    lidarMapLayer.loadingMapName,
    lidarMapLayer.loadingRobotId,
    lidarMapLayer.loadingFacilityId,
    activeMode,
    dispatch,
  ])

  const laserscanOverlayItem = buildLaserscanOverlayItem({
    localizationMode: telemetry?.localization_info?.localization_mode,
    laserscanActive,
    robotId: robot.robotInfo?.id,
    onToggle: toggleLaserscan,
  })

  const overlayItems: IOverlayItem[] = [
    ...(robot.robotInfo?.vendor === VENDOR.AUTONOMICS
      ? [
          {
            onClick: switchShowLidarMap,
            active: lidarMapLayer.show,
            disabled: !robot.telemetry?.localization_info?.current_lidar_map || activeMode === ROBOT_MODES.MODE_MAPPING,
            content: <>Lidar map</>,
          },
          ...(laserscanOverlayItem ? [laserscanOverlayItem] : []),
        ]
      : []),
  ]

  const [url, setUrl] = useUrlQueryState({
    tab: stringParam('details'),
  })
  const [map, setMap] = useState<MapboxMap | null>(null)

  const [highlightedScenarioId, setHighlightedScenarioId] = useState<number | undefined>(undefined)
  const [gausiumPreviewDismissed, setGausiumPreviewDismissed] = useState(false)
  const [gausiumScenarioRows, setGausiumScenarioRows] = useState<IScenarioInfo[]>([])
  const [gausiumMapGeoPins, setGausiumMapGeoPins] = useState<GausiumMapGeoPin[]>([])
  const lastGausiumCameraFocusScenarioId = useRef<number | undefined>(undefined)
  const gausiumScenarioRowsRef = useRef<IScenarioInfo[]>([])
  gausiumScenarioRowsRef.current = gausiumScenarioRows

  const isGausium = robot.robotInfo?.vendor === VENDOR.GAUSIUM
  const isAsbis = robot.robotInfo?.vendor === VENDOR.ASBIS
  const robotId = robot.robotInfo?.id
  const liveExecution = useMemo(() => {
    const raw = robot.robotInfo?.robotRaw
    return raw ? readLiveExecutionFromRobotRead(raw as any) : undefined
  }, [robot.robotInfo?.robotRaw])
  const [liveExecutionIntervalMs, setLiveExecutionIntervalMs] = useState<number | null>(null)
  const lastLiveExecutionRecordedAtRef = useRef<number | null>(null)

  const showTasksTab = !isGausium && !isAsbis && canSeeRobotTasksTab(user?.state ?? { audience: null })
  const showServiceTab =
    !isGausium || canSeeServiceReport({ audience: user?.state.audience ?? null, vendor: robot.robotInfo?.vendor })

  const tabIndices = getRobotTabIndices(
    robot.robotInfo?.vendor,
    GAUSIUM_ENABLED,
    showTasksTab,
    showServiceTab
  )
  const { detailsIndex, controlIndex, serviceIndex, scenariosIndex, historyIndex } = tabIndices
  const tab = robotTabIndexFromName(url.tab, tabIndices)

  const facilityId = robot.robotInfo?.facilityId
  const onServiceTab = !isGausium && !isAsbis && tab === serviceIndex
  const {
    catalog: serviceCalibCatalog,
    loading: serviceCalibLoading,
    error: serviceCalibError,
  } = useServiceCalibCatalog(onServiceTab ? facilityId : undefined, robot.robotInfo?.robot_type)
  const [serviceCalibSelectedKey, setServiceCalibSelectedKey] = useState<string | null>(null)

  useEffect(() => {
    setServiceCalibSelectedKey(null)
  }, [facilityId, robotId])

  useEffect(() => {
    if (!onServiceTab || !map || !serviceCalibSelectedKey || !serviceCalibCatalog) {
      return
    }
    const item = serviceCalibCatalog.mapItems.find(i => i.key === serviceCalibSelectedKey)
    const coords = item?.point?.coordinates
    const focusCoordinates: Array<[number, number]> = []
    if (coords && coords.length >= 2 && Number.isFinite(coords[0]) && Number.isFinite(coords[1])) {
      focusCoordinates.push([coords[0], coords[1]])
    }
    const robotCoords = resolveRobotGeoCoordinate(telemetry?.livePosition, robot.robotInfo?.lastPosition)
    if (robotCoords) {
      focusCoordinates.push(robotCoords)
    }
    if (!focusCoordinates.length) {
      return
    }
    focusMapOnGeoPoints(map, focusCoordinates, {
      leftPanelInsetPx: mapPanelOffset,
      maxZoom: 18,
      duration: 600,
    })
  }, [onServiceTab, map, serviceCalibSelectedKey, serviceCalibCatalog, mapPanelOffset])

  useEffect(() => {
    if (!onServiceTab || !map || !serviceCalibCatalog?.mapItems.length) {
      return
    }
    const mapFeatures = buildServiceCalibMapFeatures(serviceCalibCatalog.mapItems, null)
    const coordinates = mapFeatures.features
      .map(feature => feature.geometry.coordinates as [number, number])
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))
    const robotCoords = resolveRobotGeoCoordinate(telemetry?.livePosition, robot.robotInfo?.lastPosition)
    if (robotCoords) {
      coordinates.push(robotCoords)
    }
    if (!coordinates.length) {
      return
    }
    focusMapOnGeoPoints(map, coordinates, {
      leftPanelInsetPx: mapPanelOffset,
      maxZoom: 18,
      duration: 0,
    })
  }, [onServiceTab, map, facilityId, serviceCalibCatalog, mapPanelOffset])

  const gausiumLiveMapWsEnabled =
    isGausium && GAUSIUM_ENABLED && robotId != null && (tab === detailsIndex || tab === controlIndex)
  const { liveExecutionWs, liveExecutionWsIsFresh } = useGausiumLiveExecutionWebSocket(
    robotId ?? null,
    gausiumLiveMapWsEnabled
  )

  const onGsScenariosOrHistoryTab = isGausium && GAUSIUM_ENABLED && (tab === scenariosIndex || tab === historyIndex)
  const autonomicsMappingOverlayVisible =
    robot.robotInfo?.vendor === VENDOR.AUTONOMICS && activeMode === ROBOT_MODES.MODE_MAPPING && tab === serviceIndex
  const { occupancyImage: autonomicsOccupancyImage, marker: autonomicsMarker } = useAutonomicsMappingLocalWebSocket({
    enabled: autonomicsMappingOverlayVisible,
    robotSerialNumber: robot.robotInfo?.serialNumber,
    vendor: robot.robotInfo?.vendor,
    robotColor: robot.robotInfo?.robot_color,
    robotType: robot.robotInfo?.robot_type,
    originToFrontM: robot.robotInfo?.originToFrontM,
  })

  const handleGausiumScenariosSync = useCallback((rows: IScenarioInfo[], _total: number) => {
    setGausiumScenarioRows(rows)
  }, [])

  const gausiumMapPinHighlightId = useMemo(() => {
    if (highlightedScenarioId == null) return undefined
    const row = gausiumScenarioRows.find(s => s.id === highlightedScenarioId)
    if (row?.is_gausium_executable_task && row.gausium_map_pk != null) return row.gausium_map_pk
    return highlightedScenarioId
  }, [highlightedScenarioId, gausiumScenarioRows])

  const handleScenarioSelect = useCallback(
    (id: number) => {
      if (highlightedScenarioId === id) {
        setHighlightedScenarioId(undefined)
        setGausiumPreviewDismissed(false)
        setTimeout(() => setHighlightedScenarioId(id), 0)
        return
      }
      setHighlightedScenarioId(id)
      setGausiumPreviewDismissed(false)
    },
    [highlightedScenarioId]
  )

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setUrl({ tab: robotTabNameFromIndex(newValue, tabIndices) })
    if (isGausium && GAUSIUM_ENABLED) {
      const onGsTab = newValue === scenariosIndex || newValue === historyIndex
      if (!onGsTab) {
        setHighlightedScenarioId(undefined)
      }
      if (newValue === detailsIndex || newValue === controlIndex) {
        // Keep overlay available on Status and Control.
        setGausiumPreviewDismissed(false)
      }
    }
  }

  const handleHistoryRowClick = useCallback(
    (row: NormalizedStatisticsApiRow) => {
      const isGs =
        row.vendor_origin_record_type === 'gausium_task_report' || String(row.vendor || '') === VENDOR.GAUSIUM
      if (isGs) {
        const mapId = parseNumericId(row.scenario_or_map_id)
        if (mapId != null) {
          handleScenarioSelect(mapId)
        }
      }
    },
    [handleScenarioSelect]
  )

  useEffect(() => {
    const currentState = String(liveExecution?.state || '')
      .trim()
      .toUpperCase()
    const pollIntervalMs = isGausium && currentState === 'RUNNING' ? 1500 : TIMEOUT_FETCHING_STATUS
    const interval = setInterval(async () => {
      if (robot.robotInfo) {
        try {
          const newRobot = await RobotApi.fetchRobot(robot.robotInfo.id)
          dispatch(RobotActions.setRobotInfo(newRobot))
        } catch (error) {
          if (error instanceof TypeError && error.message === 'Failed to fetch') {
            return
          }
          console.warn('[RobotView] Error fetching robot data:', error)
        }
      }
    }, pollIntervalMs)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robot.robotInfo?.id, isGausium, liveExecution?.state])

  useEffect(() => {
    const recordedAtRaw = String(liveExecution?.recorded_at || '').trim()
    if (!recordedAtRaw) return
    const currentRecordedAtMs = Date.parse(recordedAtRaw)
    if (!Number.isFinite(currentRecordedAtMs)) return
    const prevRecordedAtMs = lastLiveExecutionRecordedAtRef.current
    if (prevRecordedAtMs != null) {
      const deltaMs = Math.round(currentRecordedAtMs - prevRecordedAtMs)
      if (deltaMs > 0 && deltaMs < 120000) {
        setLiveExecutionIntervalMs(deltaMs)
      }
    }
    lastLiveExecutionRecordedAtRef.current = currentRecordedAtMs
  }, [liveExecution?.recorded_at])

  useEffect(() => {
    setHighlightedScenarioId(undefined)
    setGausiumPreviewDismissed(false)
    setGausiumScenarioRows([])
    lastGausiumCameraFocusScenarioId.current = undefined
  }, [robotId])

  useEffect(() => {
    setGausiumPreviewDismissed(false)
  }, [highlightedScenarioId])

  useEffect(() => {
    if (!GAUSIUM_ENABLED || !isGausium || robotId == null) {
      setGausiumMapGeoPins([])
      return
    }
    let cancelled = false
    MainApi.fetchGausiumMaps([robotId])
      .then(({ results }) => {
        if (cancelled) return
        const mapped = (results || []).map(m => ({
          id: m.id,
          name: m.map_name || '',
          geo_point: m.geo_point ?? null,
        }))
        setGausiumMapGeoPins(mapped)
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('[RobotView] fetchGausiumMaps (pins):', err)
          setGausiumMapGeoPins([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [isGausium, robotId])

  useEffect(() => {
    if (!GAUSIUM_ENABLED || !isGausium) return
    if (tab !== scenariosIndex && tab !== historyIndex) return
    if (!highlightedScenarioId) return
    if (gausiumScenarioRowsRef.current.some(s => s.id === highlightedScenarioId)) return
    let cancelled = false
    MainApi.fetchGausiumMaps(undefined, undefined, undefined, undefined, highlightedScenarioId)
      .then(res => {
        if (cancelled) return
        const m = res.results?.[0]
        if (!m) return
        setGausiumScenarioRows(prev => {
          if (prev.some(s => s.id === m.id)) return prev
          return [...prev, gausiumMapRowToScenarioInfo(m)]
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [highlightedScenarioId, tab, isGausium, scenariosIndex, historyIndex])

  useEffect(() => {
    if (!map || !GAUSIUM_ENABLED || !isGausium) return
    if (tab !== scenariosIndex && tab !== historyIndex) return
    if (highlightedScenarioId == null) {
      lastGausiumCameraFocusScenarioId.current = undefined
      return
    }
    const pin = gausiumMapGeoPins.find(p => Number(p.id) === Number(gausiumMapPinHighlightId))
    const gp = pin?.geo_point
    if (gp?.type !== 'Point' || !gp.coordinates || gp.coordinates.length < 2) return
    const [lng, lat] = gp.coordinates as [number, number]
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return

    if (lastGausiumCameraFocusScenarioId.current === highlightedScenarioId) return
    lastGausiumCameraFocusScenarioId.current = highlightedScenarioId

    zoomMapTo(
      map,
      [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {},
        },
      ],
      mapPanelOffset,
      17
    )
  }, [
    map,
    tab,
    highlightedScenarioId,
    gausiumMapGeoPins,
    isGausium,
    scenariosIndex,
    historyIndex,
    gausiumMapPinHighlightId,
    mapPanelOffset,
  ])

  const selectedGausiumScenarioPreview = useMemo(() => {
    if (!onGsScenariosOrHistoryTab || !highlightedScenarioId) return null
    const s = gausiumScenarioRows.find(x => x.id === highlightedScenarioId)
    if (s?.is_gausium_executable_task) {
      return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
    }
    if (!s?.is_gausium_map) return null
    return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
  }, [onGsScenariosOrHistoryTab, highlightedScenarioId, gausiumScenarioRows])

  const selectedOrLiveGausiumPreview = useMemo(() => {
    if (selectedGausiumScenarioPreview) return selectedGausiumScenarioPreview
    if (!isGausium || !GAUSIUM_ENABLED) return null
    const liveUrl = String(
      (liveExecutionWsIsFresh ? liveExecutionWs?.map?.preview_url : null) ?? liveExecution?.map?.preview_url ?? ''
    ).trim()
    if (!liveUrl) return null
    return {
      url: liveUrl,
      name: String(
        (liveExecutionWsIsFresh ? liveExecutionWs?.map?.name : null) ?? liveExecution?.map?.name ?? ''
      ).trim(),
    }
  }, [selectedGausiumScenarioPreview, isGausium, liveExecution, liveExecutionWs, liveExecutionWsIsFresh])
  const gausiumRobotMarker = useMemo<FloorMapRobotMarker | null>(() => {
    // Do not show live marker when user explicitly previews another scenario/map.
    if (selectedGausiumScenarioPreview) return null
    if (!isGausium || !robot.robotInfo) return null
    const pos =
      (liveExecutionWsIsFresh ? liveExecutionWs?.map?.position ?? null : null) ?? liveExecution?.map?.position ?? null
    if (!pos) return null
    const iconName = getRobotIconName(robot.robotInfo.vendor, robot.robotInfo.robot_color, robot.robotInfo.robot_type)
    const iconUrl = ROBOT_MAP_IMAGES.find(x => x.name === iconName)?.image
    if (!iconUrl) return null
    const headingOffsetDeg = iconName === 'robot-gs-75' ? 0 : 180
    const currentExecState =
      (liveExecutionWsIsFresh ? liveExecutionWs?.state ?? null : null) ?? liveExecution?.state ?? null
    const state = String(currentExecState || '').toUpperCase()
    const pollElapsedMs = Number(liveExecutionWs?.poll_elapsed_ms)
    const pollElapsedTransitionMs =
      liveExecutionWsIsFresh && Number.isFinite(pollElapsedMs) && pollElapsedMs > 0
        ? Math.max(200, Math.min(8000, pollElapsedMs))
        : null
    /** Must stay <= RobotView poll interval while RUNNING (1500ms), or each poll restarts mid-animation. */
    const runningPollMs = 1500
    const defaultTransitionMs = state === 'RUNNING' ? runningPollMs : 6000
    const inferredMs =
      liveExecutionIntervalMs != null && Number.isFinite(liveExecutionIntervalMs)
        ? Math.max(200, Math.min(8000, liveExecutionIntervalMs))
        : null
    const transitionMs =
      pollElapsedTransitionMs != null
        ? pollElapsedTransitionMs
        : state === 'RUNNING'
        ? Math.min(runningPollMs, inferredMs ?? runningPollMs)
        : inferredMs ?? defaultTransitionMs
    return {
      x: pos.x,
      y: pos.y,
      angleDeg: pos.angle,
      iconUrl,
      headingOffsetDeg,
      iconSizePx: 40,
      transitionMs,
    }
  }, [
    selectedGausiumScenarioPreview,
    isGausium,
    robot.robotInfo,
    liveExecution,
    liveExecutionIntervalMs,
    liveExecutionWs,
    liveExecutionWsIsFresh,
  ])
  const gausiumTrailPoints = useMemo<FloorMapTrailPoint[]>(() => {
    // Do not draw live trail when user explicitly previews another scenario/map.
    if (selectedGausiumScenarioPreview) return []
    if (!isGausium) return []
    const effectiveState = String(
      (liveExecutionWsIsFresh ? liveExecutionWs?.state : null) ?? liveExecution?.state ?? ''
    )
      .trim()
      .toUpperCase()
    if (effectiveState !== 'RUNNING') return []
    const points =
      (liveExecutionWsIsFresh ? liveExecutionWs?.trail_points : undefined) ?? liveExecution?.trail_points ?? []
    if (!Array.isArray(points)) return []
    return points
      .map(point => {
        if (!point || typeof point !== 'object') return null
        const x = Number((point as any).x)
        const y = Number((point as any).y)
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null
        return { x, y }
      })
      .filter((point): point is FloorMapTrailPoint => !!point)
  }, [selectedGausiumScenarioPreview, isGausium, liveExecution?.trail_points, liveExecutionWs, liveExecutionWsIsFresh])
  const showGausiumOverlayOnCurrentTab = tab === detailsIndex || tab === controlIndex
  const gausiumNoPreviewMessage = useMemo(() => {
    if (selectedOrLiveGausiumPreview) return undefined
    const hasTelemetryMapId = !!String(
      (liveExecutionWsIsFresh ? liveExecutionWs?.map?.external_id : null) ?? liveExecution?.map?.external_id ?? ''
    ).trim()
    if (!hasTelemetryMapId) {
      return t('gausium_map.preview_unavailable_no_map_in_telemetry')
    }
    return t('gausium_map.preview_unavailable')
  }, [selectedOrLiveGausiumPreview, liveExecution?.map?.external_id, liveExecutionWs, liveExecutionWsIsFresh, t])
  const gausiumOverlayVisible =
    isGausium && GAUSIUM_ENABLED && showGausiumOverlayOnCurrentTab && !gausiumPreviewDismissed

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) {
      setMap(m)
    }
  }

  const gausiumScenariosProps =
    isGausium && GAUSIUM_ENABLED
      ? {
          highlightedScenarioId,
          onScenarioClick: (id: number, _missionIds: Array<number | string>) => {
            handleScenarioSelect(id)
          },
          onSyncScenarioState: handleGausiumScenariosSync,
        }
      : undefined

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.titleBlock}>
          <div className={style.titleInner} style={{ pointerEvents: 'auto' }}>
            <div className={style.titleImage}>
              <RobotImage />
            </div>
            <div className={style.titleContent}>
              <div className={style.nameRow}>
                <div className={style.titleWithFavorite}>
                  <Title text={robot.robotInfo?.name || ''} className={style.mobileTitle} />
                  {robot.robotInfo?.id != null && robot.robotInfo.id > 0 && (
                    <FavoriteStar kind="robot" id={robot.robotInfo.id} />
                  )}
                </div>
                {!isMobile && (
                  <div className={style.titleId}>
                    {t('robot.serial_number')}: {robot.robotInfo?.serialNumber}
                  </div>
                )}
                {!isMobile && (
                  <RobotTypeLabel vendor={robot.robotInfo?.vendor} robotType={robot.robotInfo?.robot_type} />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={clsx(style.leftBlock, { [style.hidden]: isMobile && !showSidebar })}>
          <Box className={style.box}>
            <RobotLeftBlock
              tab={tab}
              onTabChange={handleTabChange}
              gausiumScenarios={gausiumScenariosProps}
              onHistoryRowClick={isGausium && GAUSIUM_ENABLED ? handleHistoryRowClick : undefined}
              serviceCalib={
                robot.robotInfo?.id != null && facilityId != null
                  ? {
                      robotId: robot.robotInfo.id,
                      catalog: serviceCalibCatalog,
                      loading: serviceCalibLoading,
                      error: serviceCalibError,
                      selectedKey: serviceCalibSelectedKey,
                      onSelectKey: setServiceCalibSelectedKey,
                    }
                  : undefined
              }
            />
          </Box>
        </div>
        <div
          className={clsx(style.mapWrapper, {
            [style.mapOverlayActive]: gausiumOverlayVisible || autonomicsMappingOverlayVisible,
          })}
          ref={mapWrapperRef}
        >
          <MapboxCommon
            ref={mapRef}
            className={style.map}
            onMapInited={onMapInited}
            overlayItems={overlayItems}
            searchRight={360}
            panelOffset={mapPanelOffset}
            hideSearch={true}
            extraMobileButtonsCount={1}
          >
            {onServiceTab &&
              serviceCalibCatalog &&
              serviceCalibCatalog.mapItems.length > 0 && (
                <ServiceCalibMap
                  map={map ?? mapRef.current?.getMap() ?? undefined}
                  items={serviceCalibCatalog.mapItems}
                  selectedKey={serviceCalibSelectedKey}
                  onSelectKey={setServiceCalibSelectedKey}
                />
              )}
            <RobotOnMap
              map={map ?? mapRef.current?.getMap() ?? undefined}
              offset={mapPanelOffset}
              disableCenterButton={setPoseMode}
              setPoseMode={setPoseMode}
            />
            {!isAsbis && setPoseMode && (
              <RobotOnMapEditable
                map={map ?? mapRef.current?.getMap() ?? undefined}
                position={setPosePosition}
                yaw={setPoseYaw}
                onPositionChanged={setSetPosePosition}
                onYawChanged={setSetPoseYaw}
              />
            )}
            {!isAsbis && (
              <LidarMapLayer
                show={lidarMapLayer.show}
                beforeId={setPoseMode ? 'robot-editable-image-style' : 'robot-image-style'}
              />
            )}
            {!isAsbis && (
              <LaserscanLayer
                show={laserscanActive}
                scan={laserscanData}
                robotPosition={telemetry?.livePosition ?? robot.robotInfo?.lastPosition?.coordinates}
                robotOrientation={telemetry?.quaternion ?? robot.robotInfo?.lastOrientation}
                poseFrame={robot.robotInfo?.poseFrame}
                wheelBaseM={robot.robotInfo?.wheelBaseM}
              />
            )}
            {GAUSIUM_ENABLED && !isAsbis && onGsScenariosOrHistoryTab && (
              <GausiumMapGeoPinsOnMap
                map={map ?? mapRef.current?.getMap() ?? undefined}
                pins={gausiumMapGeoPins}
                highlightedId={gausiumMapPinHighlightId}
                onPinClick={mapPinId => {
                  const taskRow = gausiumScenarioRows.find(
                    s => s.is_gausium_executable_task && Number(s.gausium_map_pk) === Number(mapPinId)
                  )
                  if (taskRow) handleScenarioSelect(taskRow.id)
                  else handleScenarioSelect(mapPinId)
                }}
              />
            )}
            {!isAsbis && (
              <SetPoseFloatingButton
                laserscanActive={laserscanActive}
                setPoseMode={setPoseMode}
                setSetPoseMode={setSetPoseMode}
                disabled={isSetPoseButtonDisabled(
                  telemetry?.localization_info?.localization_mode,
                  !!robot.telemetry?.localization_info?.current_lidar_map
                )}
                localizationMode={telemetry?.localization_info?.localization_mode}
              />
            )}
          </MapboxCommon>
          {!isAsbis &&
            isSetPoseCardVisible(setPoseMode, telemetry?.localization_info?.localization_mode, laserscanActive) && (
              <SetPoseConfirmCard onCancel={() => setSetPoseMode(false)} onConfirm={handleConfirmSetPose} />
            )}
          {isGausium && GAUSIUM_ENABLED && (
            <FloorMapPreviewOverlay
              visible={gausiumOverlayVisible}
              previewUrl={selectedOrLiveGausiumPreview?.url}
              mapName={undefined}
              mapWidgetRootRef={mapWrapperRef}
              insetLeftPx={isMobile ? 0 : SCENARIO_MAP_COLUMN_INSET_LEFT_PX}
              closeOffsetTopPx={SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX}
              dismissible={false}
              noPreviewMessage={gausiumNoPreviewMessage}
              onClose={() => setHighlightedScenarioId(undefined)}
              robotMarker={gausiumRobotMarker}
              trailPoints={gausiumTrailPoints}
            />
          )}
          {robot.robotInfo?.vendor === VENDOR.AUTONOMICS && (
            <FloorMapPreviewOverlay
              visible={autonomicsMappingOverlayVisible}
              previewUrl={autonomicsOccupancyImage?.src}
              mapWidgetRootRef={mapWrapperRef}
              insetLeftPx={isMobile ? 0 : SCENARIO_MAP_COLUMN_INSET_LEFT_PX}
              closeOffsetTopPx={SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX}
              dismissible={false}
              noPreviewMessage="Waiting for occupancy map message..."
              onClose={() => undefined}
              robotMarker={autonomicsMarker as FloorMapRobotMarker | null}
              trailPoints={[]}
              preserveViewOnSrcChange
            />
          )}
        </div>
        {isMobile && (
          <button
            className={MapTableToggleStyle.toggle}
            onClick={() => setShowSidebar((prev: boolean) => !prev)}
            title={showSidebar ? 'Switch to Map' : 'Switch to Table'}
            style={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
            }}
          >
            {showSidebar ? (
              <MapIcon className={MapTableToggleStyle.icon} />
            ) : (
              <ListAltIcon className={MapTableToggleStyle.icon} />
            )}
          </button>
        )}
      </div>
    </div>
  )
}
export default RobotView
